/**
 * Feb 2026 · Phase 1 · RM Transfer to Covering Godown
 * ---------------------------------------------------
 * Purchase & Inventory · RM Transfer to Covering
 *
 * Common WIP transfer from RM-STORE → Covering Godown (COV-4FLR).
 * Deliberately NOT linked to any SO/CWO — the covering floor holds a
 * common pool of Spandex/Yarn that is drawn down later via Level-2
 * BOM consumption on Covering Production entries.
 *
 * Feb 25 · Mandatory Batch Selection · the free-text batch field is
 * replaced by a required Batch/Lot picker sourced from the source
 * godown's active Passed lots. Post Transfer is disabled until the
 * operator explicitly chooses which lot the transfer draws from.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import {
  PackageIcon,
  ArrowRightIcon,
  TrashIcon,
  PlusIcon,
  ArrowsClockwiseIcon,
} from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const today = () => new Date().toISOString().slice(0, 10);

export default function CoveringRMTransfer() {
  const [rms, setRms] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Feb 25 · lots available for the currently-selected RM at the from-godown.
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);

  const emptyForm = {
    txn_date: today(),
    rm_code: "",
    batch_no: "",
    lot_id: "",
    qty: "",
    from_godown_code: "RM-STORE",
    to_godown_code: "COV-4FLR",
    remarks: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: g }, { data: h }] = await Promise.all([
        api.get("/raw-materials/"),
        api.get("/godowns/"),
        api.get("/covering-rm-transfers"),
      ]);
      setRms(r || []);
      setGodowns(g || []);
      setRows(h || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Feb 2026 · Covering-pool filter. Only Spandex + Covering-Yarn line
  // items are eligible to be transferred from RM Store to COV-4FLR.
  // Excludes Warp / Weft / generic Yarn categories which belong on the
  // Warping / Weaving floors. Matches:
  //   • raw_materials.category === "SPANDEX"                (all spandex)
  //   • raw_materials.name matches "covering yarn"          (branded pool item)
  // Test rows are excluded via the standard `is_active` filter — kept
  // permissive here so future variants show up automatically.
  const coveringPoolRms = useMemo(() => {
    return (rms || []).filter((r) => {
      if (r.is_active === false) return false;
      const cat  = (r.category || "").toUpperCase();
      const name = (r.name || "").toUpperCase();
      return cat === "SPANDEX" || name.includes("COVERING YARN");
    });
  }, [rms]);
  const rmSuggestions = useMemo(
    () => coveringPoolRms
      .filter((r) => {
        const q = (form.rm_code || "").toLowerCase();
        if (!q) return true;
        return (r.code || "").toLowerCase().includes(q)
            || (r.name || "").toLowerCase().includes(q);
      }),
    [coveringPoolRms, form.rm_code],
  );
  const rmGodowns = godowns.filter((g) => g.kind === "RM");
  const wipGodowns = godowns.filter(
    (g) => g.kind === "WIP" && (g.department || "").toLowerCase() === "covering",
  );

  // Feb 25 · Fetch active batches whenever the operator locks in a
  // valid RM code and source godown. Clears lot selection if either
  // changes so a stale lot can never accompany a new rm/godown.
  useEffect(() => {
    const codeMatch = rms.find(r => (r.code || "").toLowerCase() === (form.rm_code || "").toLowerCase());
    if (!codeMatch || !form.from_godown_code) {
      setLots([]);
      if (form.lot_id) setForm(f => ({ ...f, lot_id: "", batch_no: "" }));
      return;
    }
    let cancelled = false;
    setLotsLoading(true);
    api.get("/inventory/available-lots", {
      params: { rm_code: codeMatch.code, godown_code: form.from_godown_code },
    }).then(({ data }) => {
      if (cancelled) return;
      const sorted = (data || []).slice().sort((a, b) => (a.received_date || "").localeCompare(b.received_date || ""));
      setLots(sorted);
      // Reset lot selection if the previous pick is no longer valid.
      if (form.lot_id && !sorted.find(l => l.lot_id === form.lot_id)) {
        setForm(f => ({ ...f, lot_id: "", batch_no: "" }));
      }
    }).catch(() => {
      if (!cancelled) setLots([]);
    }).finally(() => { if (!cancelled) setLotsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.rm_code, form.from_godown_code, rms]);

  const pickedLot = lots.find(l => l.lot_id === form.lot_id);
  const noLots = !lotsLoading && lots.length === 0 && !!form.rm_code;

  const submit = async () => {
    if (!form.rm_code || Number(form.qty) <= 0) {
      toast.error("Pick a raw material and qty > 0");
      return;
    }
    if (!form.lot_id) {
      toast.error("Batch selection is mandatory. Please select specific batches to proceed.");
      return;
    }
    if (pickedLot && Number(form.qty) - Number(pickedLot.available_qty) > 1e-4) {
      toast.error(`Only ${fmt(pickedLot.available_qty)} ${pickedLot.uom || "kg"} available in batch ${pickedLot.batch_no}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/covering-rm-transfers", {
        ...form,
        qty: Number(form.qty),
      });
      toast.success(`${data.transfer_no} · ${fmt(data.qty)} ${data.uom} to ${data.to_godown_name}`);
      setForm({ ...emptyForm, txn_date: form.txn_date });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setBusy(false);
    }
  };

  const reverse = async (r) => {
    if (r.status === "Cancelled") return;
    if (!window.confirm(`Reverse ${r.transfer_no}? ${fmt(r.qty)} ${r.uom} moves back to ${r.from_godown_name}.`)) return;
    try {
      await api.delete(`/covering-rm-transfers/${r.id}`);
      toast.success(`${r.transfer_no} cancelled`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal failed");
    }
  };

  const inpCls =
    "w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
  const lblCls = "text-[10px] uppercase tracking-widest text-zinc-500 space-y-1 block";

  return (
    <div className="p-6 space-y-5" data-testid="crt-page">
      <div>
        <div className="overline mb-1 flex items-center gap-2">
          <PackageIcon size={11} weight="bold" />
          Purchase &amp; Inventory · RM Transfer to Covering
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight text-white">
          RM Transfer to Covering Godown
        </h1>
        <div className="text-xs text-zinc-500 mt-1">
          Common WIP transfer of Spandex / Yarn from RM Store to the Covering floor. Not tied to any SO or CWO —
          allocation happens later on the Covering Production entry via Level-2 BOM.
        </div>
      </div>

      <div className="tactical-card p-5 space-y-4" data-testid="crt-form">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <label className={lblCls}>
            <div>Date *</div>
            <input
              data-testid="crt-date"
              type="date"
              value={form.txn_date}
              onChange={(e) => setForm({ ...form, txn_date: e.target.value })}
              className={inpCls}
            />
          </label>
          <label className={lblCls}>
            <div>RM Code *
              <span className="ml-2 text-[10px] font-mono text-zinc-500 uppercase">
                {coveringPoolRms.length} Covering-pool item{coveringPoolRms.length === 1 ? "" : "s"}
              </span>
            </div>
            <input
              data-testid="crt-rm"
              list="crt-rm-list"
              value={form.rm_code}
              onChange={(e) => setForm({ ...form, rm_code: e.target.value })}
              placeholder="Type / pick Covering Yarn or Spandex"
              className={inpCls}
            />
            <datalist id="crt-rm-list">
              {rmSuggestions.map((r) => (
                <option
                  key={r.id}
                  value={r.code}
                  label={`${r.name || ""}${r.category ? ` · ${r.category}` : ""}`}
                >
                  {`${r.name || ""}${r.category ? ` · ${r.category}` : ""}`}
                </option>
              ))}
            </datalist>
            {coveringPoolRms.length === 0 && (
              <div data-testid="crt-empty-pool" className="text-[10px] text-amber-300 font-mono mt-1">
                No Covering Yarn or Spandex items in Item Master. Add them under Raw Materials with Category = SPANDEX or Name = "COVERING YARN".
              </div>
            )}
          </label>
          <label className={lblCls}>
            <div>Batch / Lot *</div>
            <select
              data-testid="crt-batch"
              value={form.lot_id}
              onChange={(e) => {
                const lot = lots.find(l => l.lot_id === e.target.value);
                setForm({ ...form, lot_id: e.target.value, batch_no: lot?.batch_no || "" });
              }}
              disabled={!form.rm_code || lotsLoading}
              className={inpCls}
            >
              <option value="">
                {lotsLoading ? "Loading batches…" : (noLots ? "No active batches" : "Select batch…")}
              </option>
              {lots.map((l) => (
                <option key={l.lot_id} value={l.lot_id}>
                  {l.batch_no} · {l.received_date} · avail {fmt(l.available_qty)} {l.uom || "kg"}
                </option>
              ))}
            </select>
            {noLots && (
              <div data-testid="crt-nolots" className="text-[10px] text-amber-300 font-mono mt-1">
                No active batches at source. Add a GRN receipt or pick a different source godown.
              </div>
            )}
          </label>
          <label className={lblCls}>
            <div>Qty (kg) *</div>
            <input
              data-testid="crt-qty"
              type="number"
              step="0.001"
              value={form.qty}
              onChange={(e) => setForm({ ...form, qty: e.target.value })}
              className={inpCls}
            />
          </label>
          <label className={lblCls}>
            <div>From Warehouse</div>
            <select
              data-testid="crt-from"
              value={form.from_godown_code}
              onChange={(e) => setForm({ ...form, from_godown_code: e.target.value })}
              className={inpCls}
            >
              {rmGodowns.map((g) => (
                <option key={g.id} value={g.code}>{g.code} · {g.name}</option>
              ))}
              {rmGodowns.length === 0 && <option value="RM-STORE">RM-STORE</option>}
            </select>
          </label>
          <label className={lblCls}>
            <div>To Covering Godown</div>
            <select
              data-testid="crt-to"
              value={form.to_godown_code}
              onChange={(e) => setForm({ ...form, to_godown_code: e.target.value })}
              className={inpCls}
            >
              {wipGodowns.map((g) => (
                <option key={g.id} value={g.code}>{g.code} · {g.name}</option>
              ))}
              {wipGodowns.length === 0 && <option value="COV-4FLR">COV-4FLR</option>}
            </select>
          </label>
          <label className={`${lblCls} md:col-span-2`}>
            <div>Remarks</div>
            <input
              data-testid="crt-remarks"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              placeholder="Optional"
              className={inpCls}
            />
          </label>
        </div>
        <div className="text-[11px] text-amber-300/80 font-mono flex items-center gap-1">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-300" />
          SO &amp; CWO stay blank at this stage · classified as{" "}
          <span className="text-amber-200">Covering WIP RM - Unallocated</span>
        </div>
        <div className="flex justify-end">
          <button
            data-testid="crt-post"
            onClick={submit}
            disabled={busy || !form.rm_code || Number(form.qty) <= 0 || !form.lot_id}
            title={!form.lot_id ? "Batch selection is mandatory. Please select specific batches to proceed." : ""}
            className="h-10 px-5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-sm inline-flex items-center gap-2"
          >
            <PlusIcon size={13} weight="bold" />
            Post Transfer
          </button>
        </div>
      </div>

      <div className="tactical-card overflow-x-auto" data-testid="crt-history">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Recent Transfers</div>
          <button
            data-testid="crt-refresh"
            onClick={load}
            disabled={loading}
            className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
          >
            <ArrowsClockwiseIcon size={11} weight="bold" /> Refresh
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">Transfer No</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">RM Code</th>
              <th className="text-left px-4 py-3">Batch / Lot</th>
              <th className="text-right px-4 py-3">Qty</th>
              <th className="text-left px-4 py-3">From → To</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`crt-row-${i}`}>
                <td className="px-4 py-3 font-mono text-[var(--accent)]">{r.transfer_no}</td>
                <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{r.txn_date}</td>
                <td className="px-4 py-3 font-mono text-zinc-300">{r.rm_code}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{r.batch_no || "—"}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-white">
                  {fmt(r.qty)} {r.uom}
                </td>
                <td className="px-4 py-3 text-zinc-300 text-xs">
                  <span className="inline-flex items-center gap-1">
                    {r.from_godown_name}
                    <ArrowRightIcon size={11} weight="bold" className="text-zinc-500" />
                    {r.to_godown_name}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest ${r.status === "Cancelled" ? "text-red-300" : "text-emerald-300"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <RowActions
                    testid={`crt-actions-${i}`}
                    items={[
                      { label: "Reverse", tone: "warn", testid: `crt-reverse-${i}`, disabled: r.status === "Cancelled", onClick: () => reverse(r) },
                    ]}
                  />
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="text-center py-16 text-zinc-500 text-xs">
                  No Covering RM transfers yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
